// Achievements screen helpers (docs/achievements/SPEC.md).
import { intlLocale, t } from './i18n';
import { plural } from './operations';

// Steps of the «days per step» chart, as on the server (decision 8).
export const STEPS = [10000, 100000, 200000, 500000, 1000000];
export const DEFAULT_STEP = 100000;

// A level reached this many days ago or less is «new» (designer's proposal, «Новый рубеж»).
const FRESH_DAYS = 30;

const DAY = 86400000;

export function formatRub(value) {
  return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Math.round(value));
}

// A level as people say it: «300 000 ₽» below a million, «2,4 млн ₽» above; two decimals only where one
// would make two levels look alike (2,25 млн).
export function formatLevel(value) {
  if (Math.abs(value) < 1000000) {
    return formatRub(value);
  }
  const digits = Math.round(value / 10000) % 10 === 0 ? 1 : 2;
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency', currency: 'RUB', notation: 'compact', maximumFractionDigits: digits,
  }).format(value);
}

// "10 тыс.", "1,2 млн": axis labels and step chips.
export function formatShortRub(value) {
  return new Intl.NumberFormat(intlLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

// "100 тыс. ₽": the step in the chart title.
export function formatStep(value) {
  return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function formatDay(date) {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${date}T00:00:00Z`));
}

export function daysLabel(days) {
  return plural(days, 'achievements.days');
}

// A plural phrase with more placeholders than the count: «последние 3 шага — в среднем по 9 дней».
export function pluralWith(count, key, params = {}) {
  const rule = new Intl.PluralRules(intlLocale()).select(count);
  return t(`${key}.${rule}`, { ...params, count: new Intl.NumberFormat(intlLocale()).format(count) });
}

function daysBetween(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);
}

export function isFresh(date, today) {
  return Boolean(date && today) && daysBetween(date, today) <= FRESH_DAYS;
}

// Levels reached after the start, oldest first.
export function reached(track) {
  return (track?.levels ?? []).filter((l) => !l.at_start);
}

export function lastReached(track) {
  return reached(track).at(-1) ?? null;
}

export function atStartLevels(track) {
  return (track?.levels ?? []).filter((l) => l.at_start);
}

// Levels with the day each one's count started: the previous level reached, or the start (decision 3).
export function withFrom(track, start) {
  let from = start;
  return (track?.levels ?? []).map((level) => {
    const row = { ...level, from: level.at_start ? null : from };
    if (!level.at_start) {
      from = level.date;
    }
    return row;
  });
}

// Share of the way from the last level reached (or zero) to the next one; 0 when the sum fell below it.
export function progress(track) {
  if (!track?.next) {
    return 1;
  }
  const last = track.levels.at(-1)?.level ?? 0;
  const span = track.next.level - last;
  return span > 0 ? Math.min(1, Math.max(0, (track.current - last) / span)) : 0;
}

// Average days of the last n finished steps: «Последние 5 шагов — в среднем 12 дней».
export function averageDays(levels, n = 5) {
  const done = levels.filter((l) => !l.at_start && l.days !== null).slice(-n);
  if (done.length === 0) {
    return null;
  }
  return { count: done.length, days: Math.round(done.reduce((sum, l) => sum + l.days, 0) / done.length) };
}

// The level reached in the fewest days, for «быстрее всего»; none when there is nothing to compare.
export function fastest(track) {
  const done = reached(track).filter((l) => l.days !== null);
  if (done.length < 2) {
    return null;
  }
  return done.reduce((best, l) => (l.days < best.days ? l : best)).level;
}

// «до 1 млн ₽ — 49 тыс. ₽»
export function untilText(track) {
  if (!track?.next) {
    return '';
  }
  return t('achievements.until', { level: formatLevel(track.next.level), sum: formatLevel(Math.max(0, track.next.remaining)) });
}

// «Около 13 нояб.», «около марта 2027», «около 2028 г.» (docs/achievements/SPEC.md, «Проект v2», forecast).
export function formatForecast(forecast) {
  if (!forecast?.date) {
    return '';
  }
  const date = new Date(`${forecast.date}T00:00:00Z`);
  if (forecast.precision === 'day') {
    return t('achievements.forecastDay', { date: new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date) });
  }
  if (forecast.precision === 'month') {
    // «марта», not «март»: the month as it stands after a day number.
    const month = new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'long', timeZone: 'UTC' })
      .formatToParts(date).find((part) => part.type === 'month')?.value ?? '';
    return t('achievements.forecastMonth', { date: `${month} ${date.getUTCFullYear()}` });
  }
  return t('achievements.forecastYear', { year: date.getUTCFullYear() });
}

// Why there is no forecast: 'short' | 'flat' | 'far'.
export function forecastReason(forecast) {
  return forecast?.reason ? t(`achievements.forecast.${forecast.reason}`) : '';
}

// «дек. 2024»
export function formatMonth(date) {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat(intlLocale(), { month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${date.length === 7 ? `${date}-01` : date}T00:00:00Z`));
}

// Signed roubles in short form: «+134 тыс. ₽» from 100 000, «−35 000 ₽» below, «+2,4 млн ₽» from a million.
export function formatSignedLevel(value) {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const abs = Math.abs(value);
  const text = abs >= 100000 && abs < 1000000
    ? new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', notation: 'compact', maximumFractionDigits: 0 }).format(abs)
    : formatLevel(abs);
  return `${sign}${text}`;
}

// A moment of «Путь»: title and the line under it.
export function pathTitle(event) {
  switch (event.kind) {
    case 'capital':
      if (event.value === 1000000) {
        return t('achievements.path.firstMillion');
      }
      return t('achievements.path.capital', { level: formatLevel(event.value) });
    case 'first_trade':
      return t('achievements.path.firstTrade', { ticker: event.ticker ?? '—' });
    case 'first_payout':
      return t(event.payout === 'coupon' ? 'achievements.path.firstCoupon' : 'achievements.path.firstDividend', { ticker: event.ticker ?? '—' });
    case 'years':
      return plural(event.value, 'achievements.path.years');
    case 'comeback':
      return t('achievements.path.comeback', { months: event.value });
    case 'streak':
      return t('achievements.path.streak', { months: event.value });
    case 'income':
      return t('achievements.path.income', { level: formatRub(event.value) });
    case 'drawdown':
      return t('achievements.path.drawdown', { depth: new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1 }).format(event.value) });
    case 'max10':
      return t('achievements.path.max10');
    case 'sectors':
      return t('achievements.path.sectors', { count: event.value });
    default:
      return '';
  }
}

export function pathSub(event) {
  const date = formatDay(event.date);
  if (event.kind === 'capital' && event.days !== null && event.days !== undefined) {
    return event.from_level
      ? t('achievements.path.capitalFrom', { date, days: daysLabel(event.days), level: formatLevel(event.from_level) })
      : t('achievements.path.capitalStart', { date, days: daysLabel(event.days) });
  }
  if (event.kind === 'first_payout' && event.value) {
    return `${date} · ${formatRub(event.value)}`;
  }
  return date;
}
